import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { CheckCircle, Sparkles, Target, TrendingUp } from 'lucide-react';

const RITHA_IMAGE = "https://customer-assets.emergentagent.com/job_beauty-training-5/artifacts/jfyvjsqp_ritha%20barreto%20academy.jpeg";

const highlights = [
  { icon: Sparkles, text: 'Técnicas exclusivas e atualizadas' },
  { icon: Target, text: 'Foco em resultado desde a primeira aula' },
  { icon: TrendingUp, text: 'Visão de negócio e posicionamento' },
  { icon: CheckCircle, text: 'Certificação reconhecida no mercado' },
];

export const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="sobre"
      ref={ref}
      data-testid="about-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-dark-paper" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative">
              {/* Decorative frame */}
              <div className="absolute -top-6 -left-6 w-full h-full border-2 border-gold/30 rounded-2xl" />
              
              {/* Main image */}
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src={RITHA_IMAGE}
                  alt="Ritha Barreto - Especialista em Estética"
                  className="w-full h-[500px] object-cover object-top"
                  data-testid="about-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-paper/80 via-transparent to-transparent" />
              </div>

              {/* Experience badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="absolute -bottom-6 -right-6 bg-gold text-black px-8 py-4 rounded-2xl shadow-xl"
              >
                <p className="text-4xl font-bold font-heading">15+</p>
                <p className="text-sm font-medium">Anos de Experiência</p>
              </motion.div>
            </div>
          </motion.div>

          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            {/* Section label */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="text-gold font-medium tracking-wider uppercase text-sm mb-4"
            >
              Sobre a Academy
            </motion.p>

            {/* Heading */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 }}
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
            >
              Transformando <span className="text-gold">Profissionais</span> em{' '}
              <span className="text-gold">Referências</span>
            </motion.h2>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 }}
              className="space-y-4 mb-8"
            >
              <p className="text-white/70 text-lg leading-relaxed">
                A <span className="text-gold font-semibold">Ritha Barreto Academy</span> nasceu da paixão por transformar vidas através da estética. Com mais de 15 anos de experiência no mercado, desenvolvemos um método único que combina técnica, prática e visão empresarial.
              </p>
              <p className="text-white/70 text-lg leading-relaxed">
                Nossa missão vai além de ensinar procedimentos: formamos profissionais completos, preparados para se destacar e construir carreiras de sucesso no mercado da beleza.
              </p>
            </motion.div>

            {/* Highlights */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 }}
              className="grid sm:grid-cols-2 gap-4 mb-8"
            >
              {highlights.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.7 + index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-10 h-10 bg-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-gold" />
                  </div>
                  <span className="text-white/80 text-sm">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Quote */}
            <motion.blockquote
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8 }}
              className="border-l-4 border-gold pl-6 py-2"
            >
              <p className="font-accent text-xl italic text-white/90">
                "A zona de conforto é um lugar delicioso, mas nada acontece lá."
              </p>
              <footer className="mt-2 text-gold font-medium">— Ritha Barreto</footer>
            </motion.blockquote>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
